import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { QuoteTemplateApiService } from '../services/quote-template-api.service';
import { QuoteTemplateResponse } from '../models/quote-template.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-quote-template-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    PageBreadcrumbComponent
  ],
  templateUrl: './quote-template-list.component.html'
})
export class QuoteTemplateListComponent implements OnInit {
  templates: QuoteTemplateResponse[] = [];
  loading = false;
  error = '';
  successMessage = '';

  constructor(
    private templateApi: QuoteTemplateApiService,
    private authService: AuthService,
    private router: Router,
    private datetimeService: DatetimeService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) { }

  ngOnInit(): void {
    this.cargarTemplates();
  }

  cargarTemplates(): void {
    this.loading = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.templateApi.getAllTemplates(businessId).subscribe({
      next: (data) => {
        this.templates = data;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar plantillas';
        this.loading = false;
        console.error('Error:', err);
      }
    });
  }

  nuevaPlantilla(): void {
    this.router.navigate(['/cotizaciones/plantillas/crear']);
  }

  editarPlantilla(template: QuoteTemplateResponse): void {
    this.router.navigate(['/cotizaciones/plantillas/editar', template.id]);
  }

  async eliminarPlantilla(template: QuoteTemplateResponse): Promise<void> {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar plantilla',
      message: `¿Está seguro de eliminar la plantilla "${template.name}"?`
    });
    if (!ok) return;
    this.templateApi.deleteTemplate(template.id, businessId).subscribe({
      next: () => {
        this.successMessage = `Plantilla "${template.name}" eliminada correctamente.`;
        this.cargarTemplates();
        this.toast.deleted('Plantilla de cotización');
      },
      error: (err) => {
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al eliminar la plantilla' });
      }
    });
  }

  previsualizar(template: QuoteTemplateResponse): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.templateApi.previewTemplate(template.id, businessId).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      },
      error: (err) => {
        this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo generar la vista previa' });
      }
    });
  }

  duplicarPlantilla(template: QuoteTemplateResponse): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.templateApi.duplicateTemplate(template.id, businessId).subscribe({
      next: (copia) => {
        this.successMessage = `Plantilla "${copia.name}" creada a partir de "${template.name}".`;
        this.cargarTemplates();
        this.toast.success({ title: 'Plantilla duplicada', message: `Se creó la plantilla "${copia.name}".` });
      },
      error: (err) => {
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al duplicar la plantilla' });
      }
    });
  }

  establecerPredeterminada(template: QuoteTemplateResponse): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.templateApi.setDefaultTemplate(template.id, businessId).subscribe({
      next: () => {
        this.successMessage = `Plantilla "${template.name}" establecida como predeterminada.`;
        this.cargarTemplates();
        this.toast.updated('Plantilla de cotización');
      },
      error: (err) => {
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al establecer plantilla predeterminada' });
      }
    });
  }

  // Eliminar plantillas requiere rol Admin o superior (el backend responde 403 para el resto).
  // Mismo patrón ad-hoc de roles usado en quotes-form (canManageLink) y call.constants.
  get canDelete(): boolean {
    const role = this.authService.getCurrentUser()?.roleName;
    return role === 'Admin' || role === 'Super Admin';
  }

  formatDate(date?: string): string {
    return this.datetimeService.formatDateNumeric(date);
  }
}
